/**
 * Money is a decimal string ("250000.00", "-500.00"), never a JS number.
 * This module is the only place allowed to do arithmetic on money (via big.js)
 * and the only place that formats it. UI code uses MoneyValue / MoneyInput.
 */
import Big from 'big.js';

Big.RM = Big.roundHalfUp;

export type Decimal = string;

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

export function isDecimalString(value: unknown): value is Decimal {
  return typeof value === 'string' && DECIMAL_RE.test(value);
}

const MINUS = '−';

const formatters = new Map<string, Intl.NumberFormat>();
function formatter(decimals: number, signDisplay: 'auto' | 'always' | 'exceptZero', compact: boolean) {
  const key = `${decimals}|${signDisplay}|${compact}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: compact ? 0 : decimals,
      maximumFractionDigits: compact ? 1 : decimals,
      signDisplay,
      roundingMode: 'halfExpand',
      numberingSystem: 'latn',
      ...(compact ? { notation: 'compact', compactDisplay: 'short' } : {}),
    });
    formatters.set(key, f);
  }
  return f;
}

export interface FormatMoneyOptions {
  decimals?: number;
  /** "always" shows + for positive values (used for differences). */
  signDisplay?: 'auto' | 'always' | 'exceptZero';
  /** Dashboards only: 18.4M. Must be requested explicitly. */
  compact?: boolean;
}

/**
 * Formats a decimal string without converting it to a JS number:
 * Intl.NumberFormat accepts the string as an exact decimal.
 * Uses a true minus sign (U+2212).
 */
export function formatMoney(value: Decimal, options: FormatMoneyOptions = {}): string {
  const { decimals = 2, signDisplay = 'auto', compact = false } = options;
  if (!isDecimalString(value)) throw new Error(`formatMoney: not a decimal string: ${String(value)}`);
  const text = formatter(decimals, signDisplay, compact).format(value as Intl.StringNumericLiteral);
  return text.replace('-', MINUS);
}

/** Normalises any decimal string to exactly `decimals` places. */
export function normalise(value: Decimal, decimals = 2): Decimal {
  return new Big(value).toFixed(decimals);
}

export function sign(value: Decimal): -1 | 0 | 1 {
  const c = new Big(value).cmp(0);
  return c < 0 ? -1 : c > 0 ? 1 : 0;
}

export const isZero = (value: Decimal) => sign(value) === 0;
export const isNegative = (value: Decimal) => sign(value) < 0;

export function add(a: Decimal, b: Decimal): Decimal {
  return new Big(a).plus(b).toFixed(2);
}
export function subtract(a: Decimal, b: Decimal): Decimal {
  return new Big(a).minus(b).toFixed(2);
}
export function negate(a: Decimal): Decimal {
  return new Big(a).times(-1).toFixed(2);
}
export function sum(values: readonly Decimal[]): Decimal {
  return values.reduce<Big>((acc, v) => acc.plus(v), new Big(0)).toFixed(2);
}
export function compare(a: Decimal, b: Decimal): -1 | 0 | 1 {
  return new Big(a).cmp(b);
}
export function min(a: Decimal, b: Decimal): Decimal {
  return compare(a, b) <= 0 ? normalise(a) : normalise(b);
}
/** amount × rate% (rate given as a percent string, e.g. "2.00"). */
export function percentOf(amount: Decimal, ratePercent: string): Decimal {
  return new Big(amount).times(ratePercent).div(100).toFixed(2);
}
/** amount × factor (factor as decimal string, e.g. VAT "0.05"). */
export function multiply(amount: Decimal, factor: string): Decimal {
  return new Big(amount).times(factor).toFixed(2);
}
/**
 * amount × part ÷ whole, rounded half-up to 2 decimals: a share in proportion (e.g. the staff part of a bank
 * clawback). Returns "0.00" when whole is zero.
 */
export function proportion(amount: Decimal, part: Decimal, whole: Decimal): Decimal {
  if (new Big(whole).eq(0)) return '0.00';
  return new Big(amount).times(part).div(whole).toFixed(2);
}
/** amount ÷ divisor (an integer count, e.g. tenor months), rounded half-up to 2 decimals. */
export function divide(amount: Decimal, divisor: number): Decimal {
  if (!Number.isInteger(divisor) || divisor === 0) throw new Error('divide: divisor must be a non-zero integer');
  return new Big(amount).div(divisor).toFixed(2);
}
/**
 * Flat-rate instalment preview: (principal + principal × rate% × months / 12) ÷ months.
 * Banks quote the real instalment; this is only for previews and mock data.
 */
export function flatInstalment(principal: Decimal, ratePercent: string, months: number): Decimal {
  const profit = new Big(principal).times(ratePercent).div(100).times(months).div(12);
  return new Big(principal).plus(profit).div(months).toFixed(2);
}

export type ParseResult = { ok: true; value: Decimal } | { ok: false; reason: 'empty' | 'invalid' };

/**
 * Parses user input from MoneyInput: strips grouping, accepts "250k" and "1.2m",
 * returns a two-decimal string. Never uses Number().
 */
export function parseMoneyInput(raw: string, { allowNegative = false } = {}): ParseResult {
  const text = raw.trim().replace(/[,\s]/g, '').replace(MINUS, '-').toLowerCase();
  if (text === '' || text === '-') return { ok: false, reason: 'empty' };
  const match = /^(-?)(\d*\.?\d*)([km]?)$/.exec(text);
  if (!match) return { ok: false, reason: 'invalid' };
  const [, neg = '', digits = '', suffix = ''] = match;
  if (digits === '' || digits === '.') return { ok: false, reason: 'invalid' };
  if (neg && !allowNegative) return { ok: false, reason: 'invalid' };
  let value = new Big(digits);
  if (suffix === 'k') value = value.times(1_000);
  if (suffix === 'm') value = value.times(1_000_000);
  if (neg) value = value.times(-1);
  return { ok: true, value: value.toFixed(2) };
}

/** Strips grouping for editing ("250,000.00" -> "250000.00"). */
export function toEditable(value: Decimal | null | undefined): string {
  if (value == null || value === '') return '';
  return value.replace(/,/g, '');
}

/** Whole-number percentage a / b × 100 (e.g. progress against a target). 0 when b is zero. */
export function percentage(a: Decimal, b: Decimal): number {
  if (new Big(b).eq(0)) return 0;
  return Math.round(new Big(a).div(b).times(100).toNumber());
}

/** a / b × 100 as a decimal string with `decimals` places (e.g. target achievement "84.6"). Null when b is zero. */
export function percentageString(a: Decimal, b: Decimal, decimals = 1): string | null {
  if (new Big(b).eq(0)) return null;
  return new Big(a).div(b).times(100).toFixed(decimals);
}

/**
 * CHART PLOTTING ONLY. Converts a decimal string to a JS number so a chart can place a mark (bar length, line point).
 * The number is never shown, summed or stored: tooltips, labels and tables keep the original decimal string and
 * render it with formatMoney / MoneyValue. Precision loss beyond ~15 significant digits is invisible at chart scale.
 * Returns null for a missing value (a gap, not a zero).
 */
export function toPlotNumber(value: Decimal | null | undefined): number | null {
  if (value === null || value === undefined || !isDecimalString(value)) return null;
  return new Big(value).toNumber();
}

/** Compact chart-axis tick for a plotted amount ("1.2M", "850K"); full amounts appear in tooltips and tables. */
export function formatPlotTick(value: number): string {
  if (!Number.isFinite(value)) return '';
  return formatMoney(new Big(value).toFixed(2), { compact: true });
}

/** A rate given as a fraction ("0.0499") as a percent string ("4.99"), trailing zeros removed (keeps 2 at least). */
export function fractionToPercent(fraction: Decimal): Decimal {
  const s = new Big(fraction).times(100).toFixed(8).replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
  return /\.\d$/.test(s) ? `${s}0` : s.includes('.') ? s : `${s}.00`;
}

/** A percent string ("4.99") as a fraction string ("0.0499"); null when it is not a non-negative decimal. */
export function percentToFraction(percent: string): Decimal | null {
  const v = percent.trim().replace(/%$/, '').trim();
  if (!/^\d+(\.\d+)?$/.test(v)) return null;
  return new Big(v).div(100).toFixed(10).replace(/0+$/, '').replace(/\.$/, '');
}
